import { MenuItem, Select } from '@src/common/components/Select';
import type { ReactNode } from 'react';
import { useId } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Option = { label: ReactNode; value: string };

interface Props<T extends FieldValues> {
  fieldName: Path<T>;
  options: readonly Option[];
  control: Control<T>;
  label?: string;
  disabled?: boolean;
}

export const FormSelect = <T extends FieldValues>({
  fieldName,
  options,
  control,
  label,
  disabled,
}: Props<T>) => {
  const fieldId = useId();
  const { t } = useTranslation();
  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field, fieldState: { error } }) => (
        <Select
          {...field}
          readOnly={disabled}
          value={
            field.value
              ? options.find((option) => field.value === option.value)?.value
              : ''
          }
          renderValue={(option: unknown) => {
            const selected = options.find((opt) => opt.value === option);
            return selected ? selected.label : '';
          }}
          error={error && t(`formErrors.${error.message}`)}
          label={label}
          id={fieldId}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
          {options.length === 0 && (
            <MenuItem key="noOption" disabled>
              {t('noOptions')}
            </MenuItem>
          )}
        </Select>
      )}
    />
  );
};
